import {
  CALCULATION_JOB_MAX_WINDOW_DAYS,
  type CalculationJob,
  type CalculationJobKind,
  type CalculationJobOrigin,
  type CalculationJobStatus,
  type TaxIssueCode,
  calculationJobKindSchema,
  calculationJobOriginSchema,
  calculationJobStatusSchema,
} from "@backchannel/tax-contracts";
import type { MessageKey } from "~/i18n/messages/pt-BR";

/**
 * Operator helpers over the `GET /v1/calculation-jobs` read model. The API
 * projects every job from immutable records; nothing here estimates a count
 * or a total, it only decides how a stored value is labelled and addressed.
 */
export type { CalculationJob, CalculationJobKind, CalculationJobOrigin, CalculationJobStatus };

export const CALCULATION_JOB_ORIGINS = calculationJobOriginSchema.options;
export const CALCULATION_JOB_STATUSES = calculationJobStatusSchema.options;

/** Origins an operator filters by. UNKNOWN is a legacy record, never a filter. */
export const FILTERABLE_JOB_ORIGINS = CALCULATION_JOB_ORIGINS.filter(
  (origin) => origin !== "UNKNOWN",
);

export const ACTIVE_JOB_STATUSES: ReadonlySet<CalculationJobStatus> = new Set([
  "QUEUED",
  "RUNNING",
]);

/** Refetch cadence while any listed job is still queued or running. */
export const CALCULATION_JOB_POLL_INTERVAL_MS = 15_000;

export function isJobActive(job: Pick<CalculationJob, "status">): boolean {
  return ACTIVE_JOB_STATUSES.has(job.status);
}

export function calculationJobsPollInterval(input: {
  items?: ReadonlyArray<Pick<CalculationJob, "status">>;
  error?: unknown;
}): number | false {
  if (input.error) return false;
  return input.items?.some(isJobActive) ? CALCULATION_JOB_POLL_INTERVAL_MS : false;
}

/* -- Drawer address ------------------------------------------------------ */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** `?job=<kind>:<jobId>` keeps the drawer linkable and reload-safe. */
export function jobKey(job: Pick<CalculationJob, "kind" | "jobId">): string {
  return `${job.kind}:${job.jobId}`;
}

export function parseJobKey(
  value: string | undefined | null,
): { kind: CalculationJobKind; jobId: string } | null {
  if (!value) return null;
  const separator = value.indexOf(":");
  if (separator < 1) return null;
  const kind = calculationJobKindSchema.safeParse(value.slice(0, separator));
  const jobId = value.slice(separator + 1);
  if (!kind.success || !UUID.test(jobId)) return null;
  return { kind: kind.data, jobId };
}

/** One lot-status vocabulary for the list, the job drawer and the lot detail. */
const LOT_STATUS_ALIASES: Record<string, string> = {
  WAITING_REVIEW: "REVIEW_REQUIRED",
  SUCCEEDED: "COMPLETED",
};

export function lotStatusMessageKey(status: string): MessageKey {
  const token = LOT_STATUS_ALIASES[status] ?? status;
  return `status.job.${token}` as MessageKey;
}

export type JobTitle =
  | { kind: "lot"; value: string }
  | { kind: "single" }
  | { kind: "workflow"; value: string };

/** Lot name first, then the lot id; a one-item calculator run is "individual". */
export function jobTitle(
  job: Pick<CalculationJob, "lot" | "origin" | "counts" | "workflowId">,
): JobTitle {
  if (job.lot) return { kind: "lot", value: job.lot.name ?? job.lot.lotId };
  if (job.origin === "CALCULATOR" && job.counts.items <= 1) return { kind: "single" };
  return { kind: "workflow", value: job.workflowId };
}

export type JobSubmitter =
  | { kind: "name"; value: string }
  | { kind: "unrecorded" }
  | { kind: "workflow"; value: string };

/**
 * Who submitted the job. A legacy caller was never recorded, so the UI says
 * so instead of guessing; a machine without a display name shows its workflow.
 */
export function jobSubmitter(
  job: Pick<CalculationJob, "submittedBy" | "origin" | "workflowId">,
): JobSubmitter {
  if (job.submittedBy.name) return { kind: "name", value: job.submittedBy.name };
  if (job.submittedBy.kind === "UNKNOWN" || job.origin === "UNKNOWN") return { kind: "unrecorded" };
  return { kind: "workflow", value: job.workflowId };
}

export function topIssues(
  issueCounts: CalculationJob["issueCounts"],
  limit = 3,
): Array<{ code: TaxIssueCode; count: number }> {
  return Object.entries(issueCounts)
    .flatMap(([code, count]) =>
      typeof count === "number" && count > 0 ? [{ code: code as TaxIssueCode, count }] : [],
    )
    .sort((left, right) => right.count - left.count || left.code.localeCompare(right.code))
    .slice(0, limit);
}

/** True when a calculation group has fewer stored batches than it announced. */
export function missingBatches(job: Pick<CalculationJob, "counts">): number {
  const { received, expected } = job.counts.batches;
  return expected === null ? 0 : Math.max(0, expected - received);
}

/* -- Period window ------------------------------------------------------- */

export const LOT_PERIODS = ["today", "7d", "30d", "custom"] as const;
export type LotPeriod = (typeof LOT_PERIODS)[number];
export const DEFAULT_LOT_PERIOD: LotPeriod = "7d";

const DAY_MS = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type LotWindow =
  | { ok: true; from: string; to: string | undefined }
  | { ok: false; reason: "incomplete" | "order" | "tooLong" };

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function localDate(iso: string): Date | null {
  if (!ISO_DATE.test(iso)) return null;
  const [year, month, day] = iso.split("-").map(Number) as [number, number, number];
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

export function resolveLotPeriod(input: {
  period?: LotPeriod;
  from?: string;
  to?: string;
}): LotPeriod {
  return input.period ?? (input.from || input.to ? "custom" : DEFAULT_LOT_PERIOD);
}

/**
 * Resolves the period chips (or a custom calendar range, inclusive of the
 * last day) to the API's half-open `[from, to)` window. Rolling periods leave
 * `to` open so a refetch still sees jobs submitted after the page opened. The
 * API refuses more than 90 days with a 422; the UI says so before asking.
 */
export function lotWindow(
  input: { period?: LotPeriod; from?: string; to?: string },
  now: Date = new Date(),
): LotWindow {
  const period = resolveLotPeriod(input);
  if (period === "today") {
    return { ok: true, from: startOfLocalDay(now).toISOString(), to: undefined };
  }
  if (period === "7d" || period === "30d") {
    const days = period === "7d" ? 7 : 30;
    return { ok: true, from: new Date(now.getTime() - days * DAY_MS).toISOString(), to: undefined };
  }
  const fromDate = input.from ? localDate(input.from) : null;
  const toDate = input.to ? localDate(input.to) : null;
  if (!fromDate || !toDate) return { ok: false, reason: "incomplete" };
  const end = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate() + 1);
  const cappedEnd = end.getTime() > now.getTime() ? now : end;
  if (fromDate.getTime() >= cappedEnd.getTime()) return { ok: false, reason: "order" };
  if (cappedEnd.getTime() - fromDate.getTime() > CALCULATION_JOB_MAX_WINDOW_DAYS * DAY_MS) {
    return { ok: false, reason: "tooLong" };
  }
  return { ok: true, from: fromDate.toISOString(), to: cappedEnd.toISOString() };
}

/**
 * Window for the stored calculations of one group. Calculations are filtered
 * by `created_at`, so the window opens at submission and closes just after
 * the last completion.
 */
export function groupCalculationWindow(job: Pick<CalculationJob, "submittedAt" | "completedAt">): {
  from: string;
  to: string;
} {
  const start = Date.parse(job.submittedAt);
  const end = Date.parse(job.completedAt ?? job.submittedAt);
  return {
    from: new Date(start - 1_000).toISOString(),
    to: new Date(Math.max(end, start) + 60_000).toISOString(),
  };
}

/**
 * The lot's tax total as every lot surface shows it (Lotes row, drawer, detail): usable rows only,
 * quantity-weighted, the lot analysis definition. Older API responses summed every stored item into
 * `totals.taxTotal` (review rows included, unweighted), so `quantityTaxTotal` wins when present.
 */
export function jobTaxTotal(
  job: Pick<CalculationJob, "quantityTaxTotal" | "totals">,
): string | null {
  return job.quantityTaxTotal ?? job.totals?.taxTotal ?? null;
}
